import { createSlice } from "@reduxjs/toolkit";
import stringUtils from "../../../../../_services/string-utils";
import { InitialIntegrationReducer } from "../../../../_services/store/InitialIntegrationReducer";

export const initialNewConfiguration = {
    clientUUID: "",
    amazonSellerId: "",
    accountingIdentifierMethod: "ORDER_NUMBER",
};

export const initialImportRequest = {
    filesToImport: [],
    accountingFromMonth: "",
    accountingToMonth: "",
    selectedMarketPlaceForFileWithoutInfo: [],
};

export const initialAmazonPayoutReducer = {
    ...new InitialIntegrationReducer(),
    newConfiguration: { ...initialNewConfiguration },
    importRequest: { ...initialImportRequest },
    adminFees: [],
    filesWithoutMarketplaceInfo: null,
    importResultInfoModal: false,
    importResultConfiguration: null,

    importSuccessMessages: null,
    importWarningMessages: [],
    importErrorMessages: [],

    isGetReportsLoading: false,
    getReportsErrorMessage: null,
    reports: null,
    deleteReportLoading: false,
    deleteReportErrorMessage: null,
    reprocessReportLoading: false,
    reprocessReportErrorMessage: null,
    resetReportLoading: false,
    resetReportErrorMessage: null,
};

const newMarketplace = {
    amazonMarketplace: "NEW",
    marketplaceName: "+",
    accountingRules: [],
    accountNumber: "1270",
    transitAccountNumber: "1367",
    reserveAccountNumber: "1594",
    hasTaxConfirmed: false,
    applyNewTaxModel: false,
};

const amazonPayoutSlice = createSlice({
    name: "AmazonPayout",
    initialState: initialAmazonPayoutReducer,
    reducers: {
        toggleNewIntegration(state) {
            state.createConfigurationModal = !state.createConfigurationModal;
        },
        toggleDeleteConfiguration(state, action) {
            state.configurationToBeDeleted = action.payload;
            state.deleteConfigurationModal = !state.deleteConfigurationModal;
        },
        toggleAdvancedSettingsModal(state) {
            state.advancedSettingsModal = !state.advancedSettingsModal;
        },
        closeImportInfoModal(state) {
            state.importResultInfoModal = false;
            state.importErrorMessages = [];
            state.importWarningMessages = [];
            state.importResultConfiguration = null;
            state.importRequest = {
                ...state.importRequest,
                selectedMarketPlaceForFileWithoutInfo: [],
            };
        },

        setEditConfiguration(state, action) {
            const result = action.payload.response ? action.payload.response : action.payload;
            state.editConfiguration = {
                ...result,
                amazonMarketplaces: [...result.amazonMarketplaces].sort((a, b) =>
                    a.amazonMarketplace.localeCompare(b.amazonMarketplace)
                ),
            };
        },

        setUnsavedChanges(state, action) {
            state.unsavedChanges = action.payload;
        },

        setCommons(state, action) {
            state.commons = action.payload && action.payload.response ? action.payload.response : action.payload;
        },

        setNewConfiguration(state, action) {
            state.newConfiguration = action.payload;
        },

        addNewConfigurationRequest(state) {
            state.submitted = true;
            state.isCreateLoading = true;
            state.createErrorMessage = null;
        },
        addNewConfigurationSuccess(state, action) {
            let config = action.payload;
            let configs = state.configurations;
            if (configs.length > 0) {
                configs = configs.concat(config);
                configs.sort((a, b) =>
                    a.clientName.toLowerCase() > b.clientName.toLowerCase()
                        ? 1
                        : a.clientName.toLowerCase() < b.clientName.toLowerCase()
                          ? -1
                          : 0
                );
            } else {
                configs = [config];
            }
            state.submitted = false;
            state.isCreateLoading = false;
            state.createConfigurationModal = false;
            state.configurations = configs;
            state.newConfiguration = initialNewConfiguration;
        },
        addNewConfigurationFailure(state, action) {
            state.submitted = false;
            state.isCreateLoading = false;
            state.createErrorMessage = action.payload;
        },

        getConfigurationsForSelectedOrganizationRequest(state) {
            state.isLoading = true;
            state.configurations = [];
            state.errorMessage = null;
        },
        fetchSellerIdSuccess(state, action) {
            state.newConfiguration.amazonSellerId = action.payload;
        },
        getConfigurationsForSelectedOrganizationSuccess(state, action) {
            state.isLoading = false;
            state.configurations = action.payload;
        },
        getConfigurationsForSelectedOrganizationFailure(state, action) {
            state.isLoading = false;
            state.configurations = [];
            state.errorMessage = action.payload;
        },

        getConfigurationRequest(state) {
            state.isLoading = true;
            state.configuration = null;
            state.errorMessage = null;
            state.unsavedChanges = false;
        },
        getConfigurationSuccess(state, action) {
            state.isLoading = false;
            state.configuration = action.payload;
        },
        getConfigurationFailure(state, action) {
            state.isLoading = false;
            state.configuration = null;
            state.errorMessage = action.payload;
        },

        updateConfigurationRequest(state) {
            state.isUpdateLoading = true;
            state.configuration = null;
            state.updateErrorMessage = null;
        },
        updateConfigurationSuccess(state, action) {
            state.isUpdateLoading = false;
            state.configuration = action.payload;
            state.successMessage = "Speichern erfolgreich.";
            state.importResultInfoModal = false;
            state.importResultConfiguration = null;
            state.unsavedChanges = false;
            state.advancedSettingsModal = false;
        },
        updateConfigurationFailure(state, action) {
            state.isUpdateLoading = false;
            state.configuration = null;
            state.updateErrorMessage = action.payload;
        },

        deleteConfigurationRequest(state) {
            state.isDeleteLoading = true;
            state.deleteErrorMessage = null;
        },
        deleteConfigurationSuccess(state, action) {
            if (state.configurations.length === 1) {
                state.configurations = [];
            } else if (state.configurations.length > 1) {
                state.configurations = state.configurations.filter(
                    (configuration) => !stringUtils.equals(configuration.externalUUID, action.payload)
                );
            }
            state.configurationToBeDeleted = null;
            state.deleteConfigurationModal = false;
            state.isDeleteLoading = false;
        },
        deleteConfigurationFailure(state, action) {
            state.isDeleteLoading = false;
            state.deleteErrorMessage = action.payload;
        },

        fetchSellersMarketplacesRequest(state, action) {
            state.isFetchMarketplacesLoading = true;
        },

        fetchSellersMarketplacesSuccess(state, action) {
            let marketplaces = [];
            action.payload.forEach((item) => {
                marketplaces.push({
                    id: item.identifier,
                    name: item.name,
                    countryCode: item.countryCode,
                    defaultCurrencyCode: item.defaultCurrencyCode,
                    domainName: item.domainName,
                    isActive: true,
                });
            });
            state.newConfiguration.marketplaces = marketplaces;
            state.isFetchMarketplacesLoading = false;
        },

        fetchSellersMarketplacesFailure(state, action) {
            state.sellersMarketplacesErrorMessage = action.payload;
            state.isFetchMarketplacesLoading = false;
        },

        deleteAmazonMarketplace(state, action) {
            const remainingAmazonMarketplaces = state.editConfiguration.amazonMarketplaces.filter(
                (marketplace) => marketplace.amazonMarketplace !== action.payload.amazonMarketplace
            );

            state.editConfiguration = {
                ...state.editConfiguration,
                amazonMarketplaces: remainingAmazonMarketplaces,
            };
        },

        activateMarketplace(state, action) {
            const selectedIdentifier = state.newConfiguration.marketplaces[action.payload].id;
            state.newConfiguration = {
                ...state.newConfiguration,
                marketplaces: state.newConfiguration.marketplaces.map((mp) => {
                    if (mp.id === selectedIdentifier) {
                        mp.isActive = !mp.isActive;
                    }
                    return mp;
                }),
            };
        },

        resetAmazonPayoutMessages(state) {
            state.successMessage = null;
            state.warningMessage = null;
            state.errorMessages = null;
            state.createErrorMessage = null;
            state.updateErrorMessage = null;
            state.deleteErrorMessage = null;
            state.importSuccessMessages = null;
            state.importSuccessMessage = null;
            state.importWarningMessages = [];
            state.importErrorMessages = [];
        },

        setDataFilesToImport(state, action) {
            state.dataFilesToImport = action.payload;
        },

        uploadDataFileAndStartImportRequest(state) {
            state.isImportLoading = true;
            state.errorMessage = null;
            state.importSuccessMessage = null;
            state.importWarningMessages = [];
            state.importErrorMessages = [];
        },
        uploadDataFileAndStartImportSuccess(state, action) {
            state.isImportLoading = false;
            state.successMessage = action.payload;
            state.dataFilesToImport = null;
        },
        uploadDataFileAndStartImportFailure(state, action) {
            state.isImportLoading = false;
            state.errorMessage = action.payload;
        },

        uploadDataFileAndValidateRequest(state) {
            state.isImportLoading = true;
            state.errorMessage = null;
            state.importSuccessMessage = null;
            state.importWarningMessages = [];
            state.importErrorMessages = [];
        },
        uploadDataFileAndValidateSuccess(state, action) {
            state.isImportLoading = false;
            state.importSuccessMessage = action.payload;
            state.importRequest = initialImportRequest;
            state.importResultInfoModal = false;
        },
        uploadDataFileAndValidateWarnings(state, action) {
            state.isImportLoading = false;
            state.importResultConfiguration = action.payload.response.updatedConfigurationDto;
            state.importRequest = {
                ...state.importRequest,
                selectedMarketPlaceForFileWithoutInfo: action.payload.response.selectedMarketPlaceForFileWithoutInfo,
            };
            state.importErrorMessages = action.payload.errorMessage.includes("\n")
                ? action.payload.errorMessage.split("\n")
                : !stringUtils.isBlank(action.payload.errorMessage)
                  ? [action.payload.errorMessage]
                  : [];
            state.importWarningMessages = action.payload.warningMessage.includes("\n")
                ? action.payload.warningMessage.split("\n")
                : !stringUtils.isBlank(action.payload.warningMessage)
                  ? [action.payload.warningMessage]
                  : [];
            state.importResultInfoModal = true;
        },
        uploadDataFileAndValidateFailure(state, action) {
            state.isImportLoading = false;
            state.importErrorMessages = action.payload.includes("\n")
                ? action.payload.split("\n")
                : !stringUtils.isBlank(action.payload)
                  ? [action.payload]
                  : [];
            state.importResultInfoModal = true;
        },

        getAdminFeesRequest(state) {
            state.isLoading = true;
        },
        getAdminFeesSuccess(state, action) {
            state.isLoading = false;
            state.adminFees = action.payload;
            state.errorMessage = null;
        },
        getAdminFeesFailure(state, action) {
            state.isLoading = false;
            state.errorMessage = action.payload;
        },

        setSelectedMarketPlaceForFileWithoutInfo(state, action) {
            state.selectedMarketPlaceForFileWithoutInfo = action.payload;
        },

        setImportRequest(state, action) {
            state.importRequest = action.payload;
        },

        closeImportResultModal(state) {
            state.importResultInfoModal = false;
        },

        getReportsRequest(state) {
            state.isGetReportsLoading = true;
            state.getReportsErrorMessage = null;
        },
        getReportsSuccess(state, action) {
            state.isGetReportsLoading = false;
            state.reports = action.payload;
        },
        getReportsFailure(state, action) {
            state.isGetReportsLoading = false;
            state.getReportsErrorMessage = action.payload;
        },
        getReportItemsRequest(state) {
            state.isgetReportItemsLoading = true;
            state.getReportItemsErrorMessage = null;
        },
        getReportItemsSuccess(state, action) {
            state.isgetReportItemsLoading = false;
            // This data is not handled in the reducer but the component itself
        },
        getReportItemsFailure(state, action) {
            state.isgetReportItemsLoading = false;
            state.getReportItemsErrorMessage = action.payload;
        },
        deleteReportRequest(state) {
            state.deleteReportLoading = true;
            state.deleteReportErrorMessage = null;
        },
        deleteReportSuccess(state, action) {
            state.deleteReportLoading = false;
        },
        deleteReportFailure(state, action) {
            state.deleteReportLoading = false;
            state.deleteReportErrorMessage = action.payload;
        },
        reprocessReportRequest(state) {
            state.reprocessReportLoading = true;
            state.reprocessReportErrorMessage = null;
        },
        reprocessReportSuccess(state, action) {
            state.reprocessReportLoading = false;
        },
        reprocessReportFailure(state, action) {
            state.reprocessReportLoading = false;
            state.reprocessReportErrorMessage = action.payload;
        },
        resetReportRequest(state) {
            state.resetReportLoading = true;
            state.resetReportErrorMessage = null;
        },
        resetReportSuccess(state, action) {
            state.resetReportLoading = false;
        },
        resetReportFailure(state, action) {
            state.resetReportLoading = false;
            state.resetReportErrorMessage = action.payload;
        },
        resetImportDataErrors(state) {
            state.deleteReportErrorMessage = null;
            state.reprocessReportErrorMessage = null;
            state.resetReportErrorMessage = null;
        },

        resetSuccessMessages(state) {
            state.successMessage = null;
            state.importSuccessMessage = null;
        },
        resetUpdateErrorMessage(state) {
            state.updateErrorMessage = null;
        },
        setUpdateErrorMessage(state, action) {
            state.updateErrorMessage = action.payload;
        },
        resetNewConfiguration(state) {
            state.newConfiguration = null;
        },
        setMissingConnectionErrorMessage(state, action) {
            if (action.payload) {
                state.missingConnectionErrorMessage = "Der Kunde ist nicht über Amazon API verbunden.";
            } else {
                state.missingConnectionErrorMessage = null;
            }
        },

        triggerImportRequest(state) {
            state.isCatchupImportLoading = true;
            state.catchupImportErrorMessage = null;
            state.catchupImportSuccessMessage = null;
        },
        triggerImportSuccess(state, action) {
            state.isCatchupImportLoading = false;
            state.catchupImportSuccessMessage = action.payload.successMessage;
        },
        triggerImportFailure(state, action) {
            state.isCatchupImportLoading = false;
            state.catchupImportErrorMessage = action.payload;
        },

        deleteMarketplaceRequest(state) {
            state.isUpdateLoading = true;
            state.updateErrorMessage = null;
            state.updateErrorMessage = null;
        },
        deleteMarketplaceSuccess(state, action) {
            state.isUpdateLoading = false;
            state.configuration = action.payload;
            state.successMessage = "Speichern erfolgreich.";
            state.importResultInfoModal = false;
            state.importResultConfiguration = null;
            state.unsavedChanges = false;
            state.advancedSettingsModal = false;
        },
        deleteMarketplaceFailure(state, action) {
            state.isUpdateLoading = false;
            state.updateErrorMessage = action.payload.errorMessage;
        },
    },
});

export default amazonPayoutSlice;
